import type {Dispatch, ReactElement, SetStateAction} from 'react'
import {useRef, useState} from 'react'
import {useLoaderData, useRouteLoaderData} from '@remix-run/react'
import {useInView} from 'react-intersection-observer'
import clsx from 'clsx'
import type {CategoriesDataProps, InViewRefCardProps, RootLoaderResponse} from '~/types/index.js'
import {getTranslation} from '~/localization/translation'
import {BrgIconArrowBack} from '~/icons/brg-icon-arrow-back'
import {BrgIconArrowForward} from '~/icons/brg-icon-arrow-forward'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {Vector} from '~/icons/vector-bottom-recommended-widget.js'
import {getDomainConfig} from '~/domains/index.js'
import SponsoredDealsCard from './sponsored-deals-card'

const CARDS_GAP_PIXELS = 16

export function SponsoredDeals() {
  const {language, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {newHomePageData} = useLoaderData() as CategoriesDataProps

  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const sponsoredDeals = newHomePageData?.homepageData?.moreSponsoredDeals
  const [refItems, setRefItems] = useState<InViewRefCardProps>({
    firstItemInView: true,
    lastItemInView: false,
  })
  const reportArrowClick = useReportWidgetClick()

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'sponsored-deals',
    widget_place: 'bottom',
  })

  const carouselRef = useRef<HTMLDivElement>(null)
  const firstCardElementRef = useRef<HTMLDivElement>(null)

  const handleScroll = (direction: 'back' | 'forward') => {
    const scrollLeft = carouselRef.current?.scrollLeft ?? 0
    const cardWidth = firstCardElementRef.current?.getBoundingClientRect().width ?? 0
    if (cardWidth) {
      carouselRef.current?.scroll({
        left:
          direction === 'back' ? scrollLeft - cardWidth - CARDS_GAP_PIXELS : cardWidth + CARDS_GAP_PIXELS + scrollLeft,
        behavior: 'smooth',
      })
      reportArrowClick({
        widget_name: direction === 'back' ? 'arrow-left-scroll' : 'arrow-right-scroll',
        widget_place: 'sponsored-deals',
      })
    }
  }
  const displayItems = sponsoredDeals && sponsoredDeals?.length > 1

  return (
    displayItems && (
      <div className={clsx('mx-auto', stylesBydomain.UTILS_MAIN_CONTAINER, 'utils_main_container')}>
        <div
          ref={widgetRef}
          className={clsx(
            'relative isolate flex min-w-full flex-col overflow-x-hidden rounded-3xl pb-7 pl-8 pt-4',
            stylesBydomain.HOMEPAGE_WIDGETS_RECOMMENDED_PRODUCTS,
            'homepage_widgets_sponsored_deals',
          )}
        >
          <Vector className="vector-bottom absolute bottom-0 left-0" />
          <Vector className="vector-top absolute right-0 top-0 rotate-180 mobile:hidden" />
          <div className="mb-6 mt-5 flex justify-between">
            <h3 className="z-10 mb-1 text-2xl font-bold leading-8">{translations.Home.SponsoredDeals.title}</h3>
            <div className="top-1 z-10 flex flex-row gap-2 pr-8 mobile:hidden">
              <button
                aria-label="show previous items"
                disabled={refItems.firstItemInView}
                data-testid="previous-button"
                type="button"
                onClick={() => handleScroll('back')}
                className={clsx(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_BG,
                  'homepage_button_arrow_bg',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_ICON_FILL,
                  'homepage_button_arrow_icon_fill',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_ICON_STROKE,
                  'homepage_button_arrow_icon_stroke',
                )}
              >
                <BrgIconArrowBack />
              </button>
              <button
                aria-label="show next items"
                type="button"
                data-testid="next-button"
                disabled={refItems.lastItemInView}
                onClick={() => handleScroll('forward')}
                className={clsx(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-300 stroke-[0.01em] disabled:pointer-events-none disabled:bg-white [&>svg>g>path]:disabled:fill-neutral-200 [&>svg>path]:disabled:fill-neutral-200',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_BG,
                  'homepage_button_arrow_bg',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_ICON_FILL,
                  'homepage_button_arrow_icon_fill',
                  stylesBydomain.HOMEPAGE_BUTTON_ARROW_ICON_STROKE,
                  'homepage_button_arrow_icon_stroke',
                )}
              >
                <BrgIconArrowForward />
              </button>
            </div>
          </div>
          <div id="carousel" className="no-scrollbar flex gap-4 overflow-x-auto p-2" ref={carouselRef}>
            {sponsoredDeals?.map((deal, i) => {
              return (
                <InViewComponent
                  key={`${deal.slug}-${i}}`}
                  setRefItems={setRefItems}
                  itemIndex={i === 0 ? 0 : i === sponsoredDeals.length - 1 ? 1 : undefined}
                >
                  <SponsoredDealsCard productInfo={deal} index={i} ref={i === 0 ? firstCardElementRef : null} />
                </InViewComponent>
              )
            })}
          </div>
        </div>
      </div>
    )
  )
}
function InViewComponent({
  itemIndex,
  setRefItems,
  children,
}: {
  itemIndex: number | undefined
  setRefItems: Dispatch<SetStateAction<InViewRefCardProps>>
  children: ReactElement
}) {
  const {ref} = useInView({
    threshold: 0.9,
    onChange: (inView) => {
      if (itemIndex === 0) {
        setRefItems((prev) => ({...prev, firstItemInView: inView}))
      }
      if (itemIndex === 1) setRefItems((prev) => ({...prev, lastItemInView: inView}))
    },
  })

  return <div ref={ref}>{children}</div>
}
